import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import SubCategoryDropdown from "./SubCategoryDropdown";
import "./ProductDropdown.css";
import { ChevronRight } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { setCurrentCategoryData, setRelatedProducts } from "../../store/slice";
import { fetchRelaPro } from "../../../src/components/util";

const ProductDrawer = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const drawerRef = useRef(null);
  const dispatch = useDispatch();
  const { products, catInfo } = useSelector((state) => state.data);
  const [openCategory, setOpenCategory] = useState(null);
  const [openSubCategory, setOpenSubCategory] = useState(null);
  const [categories, setCategories] = useState([]);

  const pathParts = location.pathname.split("/").filter(Boolean);
  let selectedCatSlug = catInfo?.cat || "";
  let selectedSubSlug = catInfo?.subCat || "";
  let selectedProductSlug = "";

  if (pathParts[0] === "product-page") {
    selectedCatSlug = pathParts[1] || selectedCatSlug;
    selectedSubSlug = pathParts[2] || selectedSubSlug;
  } else if (pathParts[0] === "Products") {
    selectedCatSlug = pathParts[1] || selectedCatSlug;
    if (pathParts.length >= 4) {
      selectedSubSlug = pathParts[2];
      selectedProductSlug = pathParts[3];
    } else if (pathParts.length === 3) {
      selectedProductSlug = pathParts[2];
    }
  }

  const selectedCategoryObj = categories.find((category) => category?.categorySlug === selectedCatSlug);
  const selectedSubName =
    selectedCategoryObj?.subCat?.find(
      (sub) => sub.subCategorySlug === selectedSubSlug || sub.subCatName === selectedSubSlug
    )?.subCatName || null;

  const handleHover = (categoryName) => {
    setOpenCategory(categoryName);
    setOpenSubCategory(categoryName === selectedCategoryObj?.category ? selectedSubName : null);
  };

  const handleClick = (categoryData) => {
    const { categorySlug, subCat = [], products: directProducts = [] } = categoryData;
    let firstProduct = directProducts?.[0];
    let matchedSubCat;
    let subCatSlug = "";

    if (!firstProduct && subCat.length > 0) {
      for (let sub of subCat) {
        if (sub.products?.length > 0) {
          matchedSubCat = sub;
          firstProduct = sub.products[0];
          subCatSlug = sub.subCategorySlug;
          break;
        }
      }
    }

    if (firstProduct) {
      dispatch(setRelatedProducts(fetchRelaPro([firstProduct], categorySlug, subCatSlug, "")));
      dispatch(setCurrentCategoryData({ cat: categorySlug, subCat: subCatSlug }));

      const route = subCatSlug
        ? `/product-page/${categorySlug}/${subCatSlug}`
        : `/product-page/${categorySlug}`;

      navigate(route, {
        state: {
          product: firstProduct,
          category: categoryData,
          ...(matchedSubCat ? { subCategory: matchedSubCat } : {}),
        },
      });
    } else {
      dispatch(setCurrentCategoryData({ cat: categorySlug, subCat: "" }));

      navigate(`/product-page/${categorySlug}`, {
        state: { category: categoryData },
      });
    }

    onClose();
  };

  useEffect(() => {
    setCategories(products?.categories || []);
  }, [products]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (drawerRef.current && !drawerRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      setOpenCategory(selectedCategoryObj?.category || null);
      setOpenSubCategory(selectedSubName);
    } else {
      setOpenCategory(null);
      setOpenSubCategory(null);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose, selectedCategoryObj?.category, selectedSubName]);

  const openCategoryData = categories.find((category) => category?.category === openCategory);

  return (
    <div className="best-dropdown">
      <div className={`product-drawer ${isOpen ? "open" : ""}`}>
        <div className="drawer-content" ref={drawerRef}>
          <button className="close-btn" onClick={onClose}>
            ✖
          </button>

          <div className="product-menu-panel">
            <ul className="product-dropdown">
              {categories.length > 0 ? (
                categories.map((category) => (
                  <li
                    key={category?.categorySlug}
                    onMouseEnter={() => handleHover(category?.category)}
                    className={`category-item ${openCategory === category?.category ? "open" : ""} ${
                      selectedCatSlug === category?.categorySlug ? "selected" : ""
                    }`}
                  >
                    <div
                      className="category-title"
                      onClick={() => handleClick(category)}
                      style={{ cursor: "pointer" }}
                    >
                      {category?.category?.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase())}
                    </div>
                    <ChevronRight size={14} className="chevron-icon" />
                  </li>
                ))
              ) : (
                <li className="loading-message">Loading categories...</li>
              )}
            </ul>

            {openCategoryData && (
              <div className="product-menu-flyouts">
                <SubCategoryDropdown
                  subCategories={openCategoryData?.subCat}
                  catProducts={openCategoryData?.products}
                  openSubCategory={openSubCategory}
                  setOpenSubCategory={setOpenSubCategory}
                  isOpen={true}
                  categoryName={
                    openCategoryData?.category?.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase()) ?? ""
                  }
                  productSlug={openCategoryData?.categorySlug ?? ""}
                  productName={
                    openCategoryData?.prodName?.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase()) ?? ""
                  }
                  catSlug={openCategoryData?.productSlug ?? ""}
                  selectedSubSlug={openCategoryData?.categorySlug === selectedCatSlug ? selectedSubSlug : ""}
                  selectedProductSlug={openCategoryData?.categorySlug === selectedCatSlug ? selectedProductSlug : ""}
                  onClose={onClose}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDrawer;
